import React from 'react'
import { keyframes } from '@emotion/react'
import { useTheme } from '@mui/material'

import TextField from './TextField'
import updateRestaurantAdditionalData from '../mutations/updateRestaurantAdditionalData'
import { handleMutationError } from '../utils/errors'

// So lange steht der Haken nach dem Speichern im Feld.
const SAVED_DURATION_MS = 1500

const popIn = keyframes`
	0% { transform: scale(0.3); opacity: 0; }
	60% { transform: scale(1.25); opacity: 1; }
	100% { transform: scale(1); }
`

const sxSavedIcon = { animation: `${popIn} 0.4s ease-out` } as const

// Telefon und E-Mail des Restaurants, die im Vertrieb dazukommen. Die Diskette erscheint, sobald ein
// Feld vom gespeicherten Stand abweicht; nach dem Speichern springt dort kurz ein grüner Haken auf.
const RestaurantAdditionalData = React.memo(
	({
		slug,
		phone: savedPhone,
		email: savedEmail,
	}: {
		slug: string
		phone: string | null
		email: string | null
	}): JSX.Element => {
		const theme = useTheme()
		const [phone, setPhone] = React.useState(savedPhone ?? '')
		const [email, setEmail] = React.useState(savedEmail ?? '')
		const [savedField, setSavedField] = React.useState<'phone' | 'email' | null>(null)

		React.useEffect(() => {
			if (!savedField) return undefined
			const timeout = setTimeout(() => setSavedField(null), SAVED_DURATION_MS)
			return () => clearTimeout(timeout)
		}, [savedField])

		const save = React.useCallback(
			async (field: 'phone' | 'email') => {
				const result = await updateRestaurantAdditionalData({ slug, phone, email })
				if (result.success) {
					setSavedField(field)
				} else {
					handleMutationError('updateRestaurantAdditionalData', result.errors)
				}
			},
			[email, phone, slug]
		)
		const handleSavePhone = React.useCallback(() => save('phone'), [save])
		const handleSaveEmail = React.useCallback(() => save('email'), [save])

		return (
			<>
				<TextField
					placeHolder="Telefon"
					size="small"
					startIconName="PhoneOutlined"
					defaultValue={savedPhone ?? ''}
					onChange={setPhone}
					endIconName={
						savedField === 'phone'
							? 'CheckCircle'
							: phone.trim() === (savedPhone ?? '')
							? undefined
							: 'SaveOutlined'
					}
					endIconColor={savedField === 'phone' ? theme.palette.success.main : undefined}
					endIconSx={savedField === 'phone' ? sxSavedIcon : undefined}
					onClickEndIcon={handleSavePhone}
					m="0.75rem 0 0 0"
				/>
				<TextField
					placeHolder="E-Mail"
					size="small"
					startIconName="MailOutlined"
					defaultValue={savedEmail ?? ''}
					onChange={setEmail}
					endIconName={
						savedField === 'email'
							? 'CheckCircle'
							: email.trim() === (savedEmail ?? '')
							? undefined
							: 'SaveOutlined'
					}
					endIconColor={savedField === 'email' ? theme.palette.success.main : undefined}
					endIconSx={savedField === 'email' ? sxSavedIcon : undefined}
					onClickEndIcon={handleSaveEmail}
					m="0.5rem 0 0 0"
				/>
			</>
		)
	}
)
RestaurantAdditionalData.displayName = 'RestaurantAdditionalData'

export default RestaurantAdditionalData
